import type {
  CommandContext,
  CommandItem,
  CommandProvider,
  CommandProviderArgs,
} from './command-provider.types';
import { COMMAND_SECTION_ORDER, COMMAND_SECTION_LABEL_KEYS } from './command-types';
import { getDeviceCommands } from './providers/devices-provider';
import { getFlowCommands } from './providers/flows-provider';
import { getHomeysCommands } from './providers/homeys-provider';
import { getLogCommands } from './providers/logs-provider';
import { getSettingsCommands } from './providers/settings-provider';
import { getStaticCommands } from './providers/static-provider';

type ScoredEntry = {
  item: CommandItem;
  score: number;
};

export const commandProviders: CommandProvider[] = [
  getStaticCommands,
  getHomeysCommands,
  getSettingsCommands,
  getDeviceCommands,
  getFlowCommands,
  getLogCommands,
];

const MAX_RESULTS = 100;

export function getCommandItems(args: CommandProviderArgs): CommandItem[] {
  const commandItems: CommandItem[] = [];

  for (const provider of commandProviders) {
    const providerItems = provider(args);

    if (providerItems.length === 0) {
      continue;
    }

    commandItems.push(...providerItems);
  }

  return commandItems;
}

export function getVisibleCommandItems({
  items,
  query,
  activeContext,
}: {
  items: CommandItem[];
  query: string;
  activeContext: CommandContext;
}): CommandItem[] {
  const normalizedQuery = normalize(query.trim());
  const shouldSortByTitle = isAlphabeticalContextSort(activeContext);
  const shouldSortBySectionAndItemOrder = isSectionAndItemContextSort(activeContext);

  if (normalizedQuery === '') {
    return getSortedCommandItems({
      items,
      shouldSortByTitle,
      shouldSortBySectionAndItemOrder,
    });
  }

  if (shouldSortBySectionAndItemOrder === true || shouldSortByTitle === true) {
    const filteredItems: CommandItem[] = [];

    for (const item of items) {
      if (getCommandScore(item, normalizedQuery) > 0) {
        filteredItems.push(item);
      }
    }

    return getSortedCommandItems({
      items: filteredItems,
      shouldSortByTitle,
      shouldSortBySectionAndItemOrder,
    });
  }

  return getTopScoredCommandItems(items, normalizedQuery);
}

export function getCommandSectionLabel(section: string, i18n: CommandProviderArgs['i18n']): string {
  const key = COMMAND_SECTION_LABEL_KEYS[section] ?? 'commandPalette.sections.command';

  return i18n.messageFormatter(key);
}

function normalize(value: unknown): string {
  if (typeof value === 'string') {
    return value.toLowerCase();
  }

  return '';
}

function getSectionSortIndex(section: string): number {
  const index = COMMAND_SECTION_ORDER.findIndex((commandSection) => {
    return commandSection === section;
  });

  return index >= 0 ? index : COMMAND_SECTION_ORDER.length;
}

function getItemSortOrder(item: CommandItem): number {
  return Number.isFinite(item.sortOrder)
    ? (item.sortOrder ?? Number.MAX_SAFE_INTEGER)
    : Number.MAX_SAFE_INTEGER;
}

function isAlphabeticalContextSort(activeContext: CommandContext): boolean {
  return (
    activeContext?.type === 'scope' &&
    (activeContext.scopeType === 'devices' ||
      activeContext.scopeType === 'flows' ||
      activeContext.scopeType === 'logs')
  );
}

function isSectionAndItemContextSort(activeContext: CommandContext): boolean {
  return activeContext?.type === 'scope' && activeContext.scopeType === 'settings';
}

function getCommandScore(command: CommandItem, normalizedQuery: string): number {
  if (normalizedQuery === '') {
    return 0;
  }

  const title = normalize(command.title);
  const subtitle = normalize(command.subtitle);

  let score = 0;

  if (title === normalizedQuery) {
    score += 1200;
  }

  if (title.startsWith(normalizedQuery)) {
    score += 900;
  }

  if (title.includes(normalizedQuery)) {
    score += 650;
  }

  if (subtitle.startsWith(normalizedQuery)) {
    score += 400;
  }

  if (subtitle.includes(normalizedQuery)) {
    score += 250;
  }

  for (const keyword of command.keywords ?? []) {
    const value = normalize(keyword);

    if (value === normalizedQuery) {
      score += 300;
      continue;
    }

    if (value.startsWith(normalizedQuery)) {
      score += 200;
      continue;
    }

    if (value.includes(normalizedQuery)) {
      score += 100;
    }
  }

  return score;
}

function sortByScoreAndSection(a: ScoredEntry, b: ScoredEntry): number {
  const scoreSort = b.score - a.score;

  if (scoreSort !== 0) {
    return scoreSort;
  }

  const sectionSort = getSectionSortIndex(a.item.section) - getSectionSortIndex(b.item.section);

  if (sectionSort !== 0) {
    return sectionSort;
  }

  const itemSort = getItemSortOrder(a.item) - getItemSortOrder(b.item);

  if (itemSort !== 0) {
    return itemSort;
  }

  return String(a.item.title).localeCompare(String(b.item.title));
}

function sortItemsByTitle(a: CommandItem, b: CommandItem): number {
  const titleSort = String(a.title).localeCompare(String(b.title));

  if (titleSort !== 0) {
    return titleSort;
  }

  const subtitleSort = String(a.subtitle ?? '').localeCompare(String(b.subtitle ?? ''));

  if (subtitleSort !== 0) {
    return subtitleSort;
  }

  return String(a.key).localeCompare(String(b.key));
}

function sortItemsBySectionAndTitle(a: CommandItem, b: CommandItem): number {
  const sectionSort = getSectionSortIndex(a.section) - getSectionSortIndex(b.section);

  if (sectionSort !== 0) {
    return sectionSort;
  }

  return sortItemsByTitle(a, b);
}

function sortItemsBySectionAndItemOrder(a: CommandItem, b: CommandItem): number {
  const sectionSort = getSectionSortIndex(a.section) - getSectionSortIndex(b.section);

  if (sectionSort !== 0) {
    return sectionSort;
  }

  const itemSort = getItemSortOrder(a) - getItemSortOrder(b);

  if (itemSort !== 0) {
    return itemSort;
  }

  return sortItemsByTitle(a, b);
}

function getSortedCommandItems({
  items,
  shouldSortByTitle,
  shouldSortBySectionAndItemOrder,
}: {
  items: CommandItem[];
  shouldSortByTitle: boolean;
  shouldSortBySectionAndItemOrder: boolean;
}): CommandItem[] {
  const sortedItems = [...items];

  if (shouldSortBySectionAndItemOrder === true) {
    sortedItems.sort(sortItemsBySectionAndItemOrder);
  } else if (shouldSortByTitle === true) {
    sortedItems.sort(sortItemsBySectionAndTitle);
  } else {
    sortedItems.sort(sortItemsBySectionAndItemOrder);
  }

  if (sortedItems.length > MAX_RESULTS) {
    sortedItems.length = MAX_RESULTS;
  }

  return sortedItems;
}

function getTopScoredCommandItems(items: CommandItem[], normalizedQuery: string): CommandItem[] {
  const topEntries: ScoredEntry[] = [];

  for (const item of items) {
    const score = getCommandScore(item, normalizedQuery);

    if (score <= 0) {
      continue;
    }

    insertScoredEntry(topEntries, { item, score });
  }

  return topEntries.map((entry) => {
    return entry.item;
  });
}

function insertScoredEntry(entries: ScoredEntry[], entry: ScoredEntry): void {
  if (
    entries.length >= MAX_RESULTS &&
    sortByScoreAndSection(entry, entries[entries.length - 1]) >= 0
  ) {
    return;
  }

  let insertIndex = entries.length;

  for (let index = 0; index < entries.length; index += 1) {
    if (sortByScoreAndSection(entry, entries[index]) < 0) {
      insertIndex = index;
      break;
    }
  }

  entries.splice(insertIndex, 0, entry);

  if (entries.length > MAX_RESULTS) {
    entries.pop();
  }
}
